/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import { Typography, Box, IconButton, Link, Button, useTheme, useMediaQuery } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoIcon from "@mui/icons-material/Info";
import { PRIMARIO } from "src/styles/styles";
import {
  useCargarArchivoMutation,
  useLazyObtenerArchivoQuery,
} from "src/redux/api/documentosSoporteApi";
import { toast } from "react-toastify";
import CustomDialog from "src/components/dialogo/CustomDialog";
import TextInput from "./TextInput";
import { useField } from "formik";

interface FileUploadProps {
  id: string;
  setValue: (value: any) => void;
  value: string;
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  proyectoId?: string;
  aspiranteId: string;
  label: string;
  nameFecha?: string;
  indications?: string;
  info?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  id,
  setValue,
  value,
  selectedFile,
  setSelectedFile,
  label,
  proyectoId,
  aspiranteId,
  nameFecha,
  indications,
  info,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [obtenerAdjunto, { isLoading: descargandoArchivo }] =
    useLazyObtenerArchivoQuery();
  const [guardarArchivo, { isLoading: guardandoArchivo }] =
    useCargarArchivoMutation();
  const [showInfo, setShowInfo] = useState(false);

  // Usar useField para acceder a los errores del campo, ignoramos field
  const [, meta] = useField(id);
  const error = meta.error;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      onUpload(file);
    }
  };

  const handleDownload = async () => {
    if (!value || value === "") return;
    
    try {
      const data = await obtenerAdjunto({ adjuntoId: value }).unwrap();
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "archivo");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
    }
  };

  const onUpload = async (file: File) => {
    if (!file) return;
    
    try {
      const response = await guardarArchivo({ file, aspiranteId }).unwrap();
      setValue(response.adjuntoId);
      toast.success("Archivo cargado exitosamente");
    } catch (error) {
      setSelectedFile(null);
      console.error("Error al cargar el archivo:", error);
    }
  };

  // Extract common styles to reduce complexity
  const containerStyle = {
    border: "2px dashed #3f51b5",
    borderColor: selectedFile || (value && value !== "") ? "#4caf50" : PRIMARIO,
    padding: 1,
    textAlign: "center",
    borderRadius: 2,
    cursor: "pointer",
    backgroundColor: selectedFile || (value && value !== "") ? "#e8f5e9" : "#f5f5f5",
  };

  // Render file status information
  const renderFileStatus = () => (
    <>
      {value && value !== "" && !selectedFile && (
        <Typography variant={isMobile ? "caption" : "body2"} color="textSecondary">
          Archivo ya cargado, haga clic{" "}
          <Link 
            component="button" 
            variant={isMobile ? "caption" : "body2"}
            onClick={(e) => {
              e.preventDefault();
              handleDownload();
            }}
          >
            aquí
          </Link>{" "}
          para descargar.
        </Typography>
      )}
      
      {selectedFile && (
        <Typography variant={isMobile ? "caption" : "body2"} color="textSecondary">
          {isMobile ? "Archivo: " : "Archivo seleccionado: "}
          {isMobile && selectedFile.name.length > 25 
            ? selectedFile.name.substring(0, 25) + "..." 
            : selectedFile.name}
        </Typography>
      )}
      
      {!selectedFile && (value === "" || !value) && (
        <Typography variant={isMobile ? "caption" : "body2"} color="textSecondary">
          Ningún archivo seleccionado
          {indications && (
            <>
              {isMobile ? <br /> : ". "}
              {indications}
            </>
          )}
        </Typography>
      )}
      
      {error && (
        <Typography variant={isMobile ? "caption" : "body2"} color="error">
          {error}
        </Typography>
      )}
    </>
  );

  // Render info dialog
  const renderInfoDialog = () => (
    <CustomDialog
      mostrar={showInfo}
      onClose={() => setShowInfo(false)}
      contenido={
        <Typography sx={isMobile ? { fontSize: 14 } : {}}>
          {info}
        </Typography>
      }
      acciones={
        <Button 
          onClick={() => setShowInfo(false)}
          size={isMobile ? "small" : "medium"}
        >
          Aceptar
        </Button>
      }
      titulo=""
      tamanioMaximo={isMobile ? "sm" : "md"}
      fullWidth
    />
  );

  // Render mobile layout
  if (isMobile) {
    return (
      <Box
        sx={{
          ...containerStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <input
          accept="application/pdf"
          id={id}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        
        {/* Header with label and upload button */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          width: "100%",
          justifyContent: "flex-start" 
        }}>
          <label htmlFor={id}>
            <IconButton component="span" size="small">
              <UploadFileIcon fontSize="small" color="primary" />
            </IconButton>
          </label>
          
          <Typography 
            variant="subtitle2" 
            fontWeight="bold"
            sx={{ ml: 1 }}
          >
            {label}{" "}
            {info && (
              <IconButton
                component="span"
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowInfo(true);
                }}
                size="small"
              >
                <InfoIcon fontSize="small" color="primary" />
              </IconButton>
            )}
          </Typography>
        </Box>
        
        {/* File status */}
        <Box sx={{ pl: 1, width: "100%", textAlign: "left" }}>
          {renderFileStatus()}
        </Box>

        {/* Date Field - Full Width on Mobile */}
        {nameFecha && (
          <Box width="100%" mt={1}>
            <TextInput
              label="Fecha de expedición"
              name={nameFecha}
              type="date"
              InputLabelProps={{ 
                shrink: true,
                sx: { fontSize: '14px' }
              }}
            />
          </Box>
        )}

        {renderInfoDialog()}
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box
      sx={{
        ...containerStyle,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <input
        accept="application/pdf"
        id={id}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <label htmlFor={id}>
        <Box sx={{ display: "flex", flexDirection: "column", width: '90px' }}>
          <IconButton component="span">
            <UploadFileIcon fontSize="medium" color="primary" />
          </IconButton>
          <Typography fontSize={8}>Seleccionar archivo</Typography>
        </Box>
      </label>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          pl: 2,
          width: "90%",
          textAlign: "left",
        }}
      >
        <Typography textAlign="left" fontWeight="bold">
          {label}{" "}
          {info && (
            <IconButton
              component="span"
              sx={{ p: 0 }}
              onClick={() => setShowInfo(true)}
            >
              <InfoIcon fontSize="small" color="primary" />
            </IconButton>
          )}
        </Typography>
        {renderFileStatus()}
      </Box>
      {nameFecha && (
        <Box width="30%">
          <TextInput
            label="Fecha de expedición"
            name={nameFecha}
            type="date"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}
      {renderInfoDialog()}
    </Box>
  );
};

export default FileUpload;